/**
 * Table - Tabular data display
 *
 * Usage:
 *   <Table
 *     title="Recent Orders"
 *     data={{
 *       columns: [
 *         { key: 'id', label: 'Order #' },
 *         { key: 'customer', label: 'Customer' },
 *         { key: 'total', label: 'Total', align: 'right' },
 *       ],
 *       rows: [
 *         { id: '1001', customer: 'John Doe', total: '$125.00' },
 *         ...
 *       ]
 *     }}
 *   />
 */

import type { BlockProps, TableData } from './types';
import './blocks.css';

export interface TableProps extends BlockProps<TableData> {
  /** Maximum rows to display (pagination handled externally) */
  maxRows?: number;
  /** Striped rows */
  striped?: boolean;
  /** Compact mode */
  compact?: boolean;
  /** Row click handler */
  onRowClick?: (row: Record<string, string | number>, index: number) => void;
}

export function Table({
  data,
  title,
  maxRows,
  striped = false,
  compact = false,
  onRowClick,
  className,
}: TableProps) {
  const { columns = [], rows = [] } = data ?? {};

  if (!rows || rows.length === 0) {
    return (
      <div className={`block-table ${className || ''}`}>
        {title && <div className="block-table-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const displayRows = maxRows ? rows.slice(0, maxRows) : rows;

  return (
    <div className={`block-table ${className || ''}`}>
      {title && <div className="block-table-title">{title}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align || 'left',
                    width: col.width,
                    padding: compact ? '6px 12px' : undefined,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row, rowIndex);
                  }
                } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                aria-label={onRowClick ? `Select row ${rowIndex + 1}` : undefined}
                style={{
                  cursor: onRowClick ? 'pointer' : undefined,
                  background: striped && rowIndex % 2 === 1 ? 'var(--color-surface-muted)' : undefined,
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      textAlign: col.align || 'left',
                      padding: compact ? '8px 12px' : undefined,
                    }}
                  >
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {maxRows && rows.length > maxRows && (
        <div style={{
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          borderTop: '1px solid var(--color-border)',
        }}>
          Showing {maxRows} of {rows.length} rows
        </div>
      )}
    </div>
  );
}

export default Table;
